"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EagleFileType = void 0;
exports.getEagleFileType = getEagleFileType;
exports.validateEagleFile = validateEagleFile;
exports.detectEagleProjects = detectEagleProjects;
const xml_js_1 = require("xml-js");
const project_detection_1 = require("../project-detection");
var EagleFileType;
(function (EagleFileType) {
    EagleFileType["EagleBrd"] = ".brd";
    EagleFileType["EagleSch"] = ".sch";
    EagleFileType["EagleLbr"] = ".lbr";
    EagleFileType["EagleDru"] = ".dru";
})(EagleFileType || (exports.EagleFileType = EagleFileType = {}));
function getEagleFileType(filename, fileContents) {
    const path = filename.split('/');
    if (path.length < 1) {
        return false;
    }
    const parts = path[path.length - 1].split('.');
    if (parts.length < 1) {
        return false;
    }
    const ext = '.' + parts[parts.length - 1].toLowerCase();
    switch (ext) {
        case '.brd':
        case '.sch':
        case '.lbr':
            if (fileContents.trim().startsWith('<?xml') && fileContents.indexOf('eagle') !== -1) {
                return ext;
            }
            break;
        case '.dru':
            return EagleFileType.EagleDru;
    }
    return false;
}
function eagleSemanticType(type) {
    switch (type) {
        case EagleFileType.EagleSch:
            return 'schematic';
        case EagleFileType.EagleBrd:
            return 'board';
        case EagleFileType.EagleLbr:
            return 'lib';
        case EagleFileType.EagleDru:
            return 'dru';
        default:
            return 'other';
    }
}
function validateEagleFile(filename, fileContents) {
    const type = getEagleFileType(filename, fileContents);
    if (!type) {
        return {
            isValid: false,
            errors: [`Invalid eagle file or contents ${filename}`],
        };
    }
    const semanticType = eagleSemanticType(type);
    try {
        const validationResult = validateEagleFileContents(fileContents, type);
        if (!validationResult) {
            return {
                isValid: false,
                semanticType,
                errors: [`Invalid eagle file or contents ${filename}`],
            };
        }
        return {
            isValid: true,
            data: {
                semanticType: validationResult.type || 'other',
                fileFormat: {
                    cadTool: 'eagle',
                    type: type,
                    version: validationResult?.version,
                },
                referencedFiles: [],
                encoding: validationResult?.encoding,
                metadata: {},
            },
        };
    }
    catch (e) {
        if (e instanceof Error || (e && typeof e === 'object' && typeof e.message === 'string')) {
            return {
                isValid: false,
                semanticType,
                errors: [e.message],
            };
        }
    }
    return {
        isValid: false,
        semanticType,
        errors: ['Could not parse file'],
    };
}
function detectEagleProjects(files, fileDetails) {
    const eagleFiles = fileDetails.filter((d) => d.cadTool === 'eagle');
    const warnings = [];
    const blobMap = (0, project_detection_1.buildBlobMap)(files);
    if (eagleFiles.length === 0) {
        return { projects: [], warnings };
    }
    const groupsByKey = new Map();
    for (const detail of eagleFiles) {
        const lower = detail.relativePath.toLowerCase();
        if (!lower.endsWith('.sch') && !lower.endsWith('.brd')) {
            continue;
        }
        const dir = (0, project_detection_1.dirnameForward)(detail.relativePath);
        const base = (0, project_detection_1.basenameWithoutExtension)(detail.relativePath);
        const key = (0, project_detection_1.normalizePathForCompare)(`${dir}/${base}`);
        if (!groupsByKey.has(key)) {
            groupsByKey.set(key, {
                name: dir === '' || dir === '.' ? base : `${dir}/${base}`,
                homeDir: dir,
                baseKey: base,
                files: [],
            });
        }
    }
    if (groupsByKey.size === 0) {
        warnings.push('No Eagle schematic or board files found to anchor a project definition.');
        return {
            projects: [(0, project_detection_1.buildProjectDefinition)('eagle', undefined, eagleFiles, blobMap)],
            warnings,
        };
    }
    const groups = Array.from(groupsByKey.values());
    for (const detail of eagleFiles) {
        const lower = detail.relativePath.toLowerCase();
        const dir = (0, project_detection_1.dirnameForward)(detail.relativePath);
        const base = (0, project_detection_1.basenameWithoutExtension)(detail.relativePath);
        if (lower.endsWith('.sch') || lower.endsWith('.brd')) {
            const target = groups.find((g) => (0, project_detection_1.normalizePathForCompare)(g.homeDir) === (0, project_detection_1.normalizePathForCompare)(dir) &&
                g.baseKey.toLowerCase() === base.toLowerCase());
            if (target) {
                target.files.push(detail);
            }
            continue;
        }
        const sameDir = groups.filter((g) => (0, project_detection_1.normalizePathForCompare)(g.homeDir) === (0, project_detection_1.normalizePathForCompare)(dir));
        if (sameDir.length > 0) {
            for (const g of sameDir) {
                g.files.push(detail);
            }
            continue;
        }
        let best;
        let bestLen = -1;
        for (const g of groups) {
            if ((0, project_detection_1.isSameOrDescendantDir)(dir, g.homeDir) && g.homeDir.length > bestLen) {
                best = g;
                bestLen = g.homeDir.length;
            }
        }
        (best ?? groups[0]).files.push(detail);
    }
    const projects = groups.map((g) => (0, project_detection_1.buildProjectDefinition)('eagle', g.name, g.files, blobMap));
    return { projects, warnings };
}
function validateEagleFileContents(fileContents, type) {
    if (type === EagleFileType.EagleDru) {
        return {
            type: 'dru',
            version: '*',
            encoding: 'utf8',
        };
    }
    const xml = (0, xml_js_1.xml2js)(fileContents, { compact: true, addParent: false });
    let result;
    if (typeof xml.eagle === 'object') {
        let brdRoot = xml;
        result = {
            encoding: brdRoot._declaration._attributes.encoding,
            version: brdRoot.eagle._attributes.version,
        };
    }
    if (!result) {
        return false;
    }
    switch (type) {
        case EagleFileType.EagleSch: {
            const schXml = xml;
            if (!schXml.eagle.drawing.schematic) {
                return false;
            }
            result.type = 'schematic';
            break;
        }
        case EagleFileType.EagleBrd: {
            const brdXml = xml;
            if (!brdXml.eagle.drawing.board) {
                return false;
            }
            result.type = 'board';
            break;
        }
        case EagleFileType.EagleLbr: {
            const libXml = xml;
            if (!libXml.eagle.drawing.library) {
                return false;
            }
            result.type = 'lib';
            break;
        }
    }
    return result;
}
//# sourceMappingURL=eagleValidation.js.map